import { useEffect, useRef, useState } from 'react'

const VIDEO_FADE_START_MS = 4500
const VIDEO_FADE_MS = 500
const QUICK_FADE_MS = 420
const FAILSAFE_MS = 6500

// A full document launch should show the OPS intro every time. The only
// intentional skip is the immediate return from Admin Gateway authentication,
// otherwise a first-time user would see the intro twice: once before login and
// again seconds later after the token redirect.
function shouldSkipIntro() {
  try {
    return new URLSearchParams(window.location.search).has('token')
  } catch {
    return false
  }
}

export function OpeningIntro({ onComplete }: { onComplete: () => void }) {
  const [skip] = useState(shouldSkipIntro)
  const [fading, setFading] = useState(false)
  const [fadeMs, setFadeMs] = useState(VIDEO_FADE_MS)
  const [reducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  const finishing = useRef(false)
  const finishTimer = useRef<number | null>(null)

  const finish = (duration = VIDEO_FADE_MS) => {
    if (finishing.current) return
    finishing.current = true
    setFadeMs(duration)
    setFading(true)
    finishTimer.current = window.setTimeout(onComplete, duration)
  }

  useEffect(() => {
    if (skip) {
      onComplete()
      return
    }

    const fallback = window.setTimeout(() => finish(QUICK_FADE_MS), FAILSAFE_MS)
    const fadeStart = reducedMotion
      ? window.setTimeout(() => finish(QUICK_FADE_MS), 900)
      : window.setTimeout(() => finish(VIDEO_FADE_MS), VIDEO_FADE_START_MS)

    return () => {
      window.clearTimeout(fallback)
      window.clearTimeout(fadeStart)
      if (finishTimer.current !== null) window.clearTimeout(finishTimer.current)
    }
  }, [skip, reducedMotion])

  if (skip) return null

  return (
    <div
      aria-label="OPS by BakersGuild opening"
      className="fixed inset-0 flex items-center justify-center overflow-hidden bg-white"
      style={{
        zIndex: 10000,
        opacity: fading ? 0 : 1,
        transition: `opacity ${fadeMs}ms cubic-bezier(0.22, 0.61, 0.36, 1)`,
        pointerEvents: 'auto',
      }}
    >
      {reducedMotion ? (
        <div className="select-none text-center text-[#101a44]">
          <div className="text-[clamp(4rem,18vw,11rem)] font-black leading-none tracking-[-0.08em]">OPS</div>
          <div className="mt-3 text-[clamp(0.85rem,2vw,1.25rem)] font-medium tracking-[0.2em] text-[#354579]">
            by BakersGuild
          </div>
        </div>
      ) : (
        <video
          autoPlay
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          className="h-full w-full bg-white object-contain"
          onEnded={() => finish(QUICK_FADE_MS)}
          onError={() => finish(QUICK_FADE_MS)}
        >
          <source src="/ops-intro.mp4?v=20260925-3" type="video/mp4" />
        </video>
      )}
    </div>
  )
}
